import axios from 'axios';
import { useVapCoreFor } from '@/services/backend';

/**
 * Axios instance for the VapCore API.
 *
 * Differences from the iGard client used elsewhere in this app:
 *  - auth is a JWT bearer token, not an `X-Auth-Token` header
 *  - responses are bare bodies, not a `{ success, data, error }` envelope
 *  - failures come back with real status codes, not a blanket 500
 */
const client = axios.create({
  baseURL: process.env.VUE_APP_VAPCORE_API_BASE_URL,
});

// Sign-in, verification and branding are anonymous, and a persisted token from an earlier
// session is not a credential for them. Sending one only produces a failed token validation on
// the server before the endpoint runs anyway.
const ANONYMOUS_PATHS = [
  '/api/care-portal/auth/',
  '/api/care-portal/branding',
];

client.interceptors.request.use((config) => {
  if (ANONYMOUS_PATHS.some((path) => config.url?.startsWith(path))) {
    return config;
  }

  // While sign-in is still served by iGard the stored token is an iGard session token, which
  // VapCore has no way to validate. Sending it would turn a clear "not signed in" into a
  // confusing signature failure, so the token only travels once VapCore is the one issuing it.
  if (!useVapCoreFor('auth')) {
    return config;
  }

  const { useCustomerStore } = require('@/store/customer');
  const token = useCustomerStore().token;

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

/**
 * Retries a read that failed for a reason worth retrying.
 *
 * Only GETs, and only on a dropped connection or a 5xx — a request that never got an answer, or
 * got one the server itself called a failure. A 4xx is the server's considered response and
 * repeating it changes nothing.
 *
 * Writes are never retried. A submission that timed out may well have succeeded, and sending it
 * again would raise a second claim.
 */
const MAX_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 300;

function isRetryable(error) {
  if (error.config?.method?.toLowerCase() !== 'get') {
    return false;
  }

  // No response at all: the connection dropped, timed out, or never landed.
  if (!error.response) {
    return true;
  }

  return error.response.status >= 500;
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!isRetryable(error)) {
      return Promise.reject(error);
    }

    const config = error.config;
    config.retryCount = config.retryCount || 0;

    if (config.retryCount >= MAX_RETRIES) {
      return Promise.reject(error);
    }

    config.retryCount += 1;
    await wait(RETRY_BASE_DELAY_MS * 2 ** (config.retryCount - 1));

    return client(config);
  }
);

export default client;
